import type { ResumeDraft, ResumeEvaluation, ResumeJobStatus } from "@hr/contracts";
import { renderPdfToImages } from "./pdf-render";

export const API_ORIGIN = import.meta.env.VITE_API_ORIGIN ?? "http://localhost:8787";

export interface UserSession {
  tenantKey: string;
  openId: string;
  name: string;
  avatarUrl: string | null;
  expiresAt: number;
}

export interface UploaderRef {
  openId: string;
  name: string;
  avatarUrl: string | null;
}

export interface PerformanceRow {
  openId: string;
  name: string;
  avatarUrl: string | null;
  total: number; // 入库候选人
  interviewed: number; // 约面试（含已发 offer）
  offered: number; // 已发 offer
  highMatch: number; // 岗位匹配度 ≥ 80
}

export interface ImportBatch {
  id: string;
  name: string;
  createdAt: string;
  jobCount: number;
  jobDescription: string | null;
  autoConfirm: boolean;
  positionId: string | null;
  positionTitle: string | null;
}

export interface ResumeJob {
  id: string;
  batchId: string;
  filename: string;
  fileSize: number;
  r2Key: string;
  status: ResumeJobStatus;
  draft: ResumeDraft | null;
  evaluation: ResumeEvaluation | null;
  errorCode: string | null;
  errorMessage: string | null;
  candidateId: string | null;
  duplicateCandidateId: string | null;
  createdAt: string;
  updatedAt: string;
}

export type Candidate = ResumeDraft & {
  id: string;
  resumeR2Key: string;
  createdAt: string;
  updatedAt: string;
  uploadedBy: UploaderRef;
  evaluationScore: number | null;
  recommendation: string | null;
  evaluation: ResumeEvaluation | null;
  positionId: string | null;
  stage: string;
  // 人工覆盖（为空表示沿用 AI 评分）
  manualScore: number | null;
  manualRecommendation: string | null;
  manualAdjustedBy: string | null;
  manualAdjustedAt: string | null;
};

export type CandidateStage = "not_interviewed" | "interviewed" | "offered" | "rejected";

export function updateCandidateStage(candidateId: string, stage: CandidateStage) {
  return request<{ ok: boolean }>(`/v1/candidates/${candidateId}/stage`, {
    method: "PUT",
    body: JSON.stringify({ stage }),
  });
}

export function updateCandidatePosition(candidateId: string, positionId: string) {
  return request<{ ok: boolean; reevaluating: boolean }>(`/v1/candidates/${candidateId}/position`, {
    method: "PUT",
    body: JSON.stringify({ positionId }),
  });
}

interface ApiErrorBody {
  code: string;
  message: string;
  details?: unknown;
  requestId: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody,
  ) {
    super(body.message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_ORIGIN}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(init.body && !(init.body instanceof Blob) && !(init.body instanceof FormData)
        ? { "content-type": "application/json" }
        : {}),
      ...init.headers,
    },
  });
  const body = (await response.json().catch(() => null)) as T | ApiErrorBody | null;
  if (!response.ok) {
    throw new ApiError(
      response.status,
      body && typeof body === "object" && "code" in body
        ? (body as ApiErrorBody)
        : { code: "UNKNOWN_ERROR", message: "请求失败", requestId: "unknown" },
    );
  }
  return body as T;
}

export function getMe() {
  return request<{ user: UserSession }>("/v1/me");
}

export function devLogin() {
  return request<{ user: UserSession }>("/v1/auth/dev-login", {
    method: "POST",
    body: JSON.stringify({
      tenantKey: "local-tenant",
      openId: "local-recruiter",
      name: "本地招聘用户",
    }),
  });
}

export function logout() {
  return request<{ ok: boolean }>("/v1/logout", { method: "POST" });
}

export function getPerformance() {
  return request<{ items: PerformanceRow[] }>("/v1/performance");
}

export function listBatches() {
  return request<{ items: ImportBatch[] }>("/v1/import-batches");
}

export function createBatch(
  name: string,
  opts: { positionId?: string; jobDescription?: string; autoConfirm?: boolean } = {},
) {
  const body: {
    name: string;
    positionId?: string;
    jobDescription?: string;
    autoConfirm: boolean;
  } = { name, autoConfirm: opts.autoConfirm ?? true };
  if (opts.positionId) body.positionId = opts.positionId;
  if (opts.jobDescription?.trim()) body.jobDescription = opts.jobDescription;
  return request<ImportBatch>("/v1/import-batches", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function getBatch(batchId: string) {
  return request<{ items: ResumeJob[] }>(`/v1/import-batches/${batchId}`);
}

export async function uploadResume(batchId: string, file: File, idempotencyKey: string) {
  // 视觉抽取：先在浏览器把 PDF 每页渲成图片，再与原 PDF 一起 multipart 上传
  const pages = await renderPdfToImages(file);
  if (pages.length === 0) throw new Error("无法渲染该 PDF 的页面，请确认文件未损坏");
  const form = new FormData();
  form.append("pdf", file, file.name);
  pages.forEach((blob, index) => form.append("pages", blob, `page-${index + 1}.jpg`));
  return request<ResumeJob>(`/v1/import-batches/${batchId}/resumes`, {
    method: "POST",
    body: form,
    headers: {
      "x-filename": encodeURIComponent(file.name),
      "idempotency-key": idempotencyKey,
    },
  });
}

export function saveDraft(jobId: string, draft: ResumeDraft) {
  return request<ResumeJob>(`/v1/resume-jobs/${jobId}/draft`, {
    method: "PUT",
    body: JSON.stringify(draft),
  });
}

export function retryJob(jobId: string) {
  return request<ResumeJob>(`/v1/resume-jobs/${jobId}/retry`, { method: "POST", body: "{}" });
}

export function deleteJob(jobId: string) {
  return request<void>(`/v1/resume-jobs/${jobId}`, { method: "DELETE" });
}

export function confirmJob(jobId: string, overwriteCandidateId?: string) {
  return request<Candidate>(`/v1/resume-jobs/${jobId}/confirm`, {
    method: "POST",
    body: JSON.stringify(overwriteCandidateId ? { overwriteCandidateId } : {}),
  });
}

export function evaluateJob(jobId: string) {
  return request<{ evaluation: ResumeEvaluation }>(`/v1/resume-jobs/${jobId}/evaluate`, {
    method: "POST",
    body: "{}",
  });
}

export function getEvaluation(jobId: string) {
  return request<{ evaluation: ResumeEvaluation }>(`/v1/resume-jobs/${jobId}/evaluation`);
}

export interface CandidateListOptions {
  sort?: "recent" | "score";
  recommendation?: string;
  positionId?: string;
  uploader?: string;
  stage?: string[];
  minMatch?: number;
}

export function listCandidates(query = "", cursor?: string, options: CandidateListOptions = {}) {
  const params = new URLSearchParams();
  if (query) params.set("q", query);
  if (cursor) params.set("cursor", cursor);
  if (options.sort === "score") params.set("sort", "score");
  if (options.recommendation) params.set("recommendation", options.recommendation);
  if (options.positionId) params.set("position", options.positionId);
  if (options.uploader) params.set("uploader", options.uploader);
  if (options.stage?.length) params.set("stage", options.stage.join(","));
  if (options.minMatch) params.set("minMatch", String(options.minMatch));
  return request<{ items: Candidate[]; nextCursor: string | null }>(`/v1/candidates?${params}`);
}

export function dismissJobs(ids: string[]) {
  return request<{ dismissed: number }>("/v1/resume-jobs/dismiss", {
    method: "POST",
    body: JSON.stringify({ ids }),
  });
}

export function getCandidate(candidateId: string) {
  return request<Candidate>(`/v1/candidates/${candidateId}`);
}

/** 人工覆盖 AI 评分/推荐结论（管理员或本人）。两者传 null 即恢复 AI 评分。 */
export function setEvaluationOverride(
  candidateId: string,
  input: { score: number | null; recommendation: string | null },
) {
  return request<Candidate>(`/v1/candidates/${candidateId}/evaluation-override`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

/** 删除候选人（仅管理员）。传一个或多个 id。 */
export function deleteCandidates(ids: string[]) {
  return request<{ deleted: number }>("/v1/candidates/delete", {
    method: "POST",
    body: JSON.stringify({ ids }),
  });
}

export function jobResumeUrl(jobId: string) {
  return `${API_ORIGIN}/v1/resume-jobs/${jobId}/resume`;
}

export function candidateResumeUrl(candidateId: string) {
  return `${API_ORIGIN}/v1/candidates/${candidateId}/resume`;
}

// ---- 岗位（positions）----

export interface Position {
  id: string;
  title: string;
  requirements: string;
  interviewerGuidance: string;
  createdAt: string;
  updatedAt: string;
}

export interface PositionMatch {
  candidate: Candidate;
  matchScore: number;
  reason: string;
}

export function listPositions() {
  return request<{ items: Position[] }>("/v1/positions");
}

export function getPosition(positionId: string) {
  return request<Position>(`/v1/positions/${positionId}`);
}

export function createPosition(
  title: string,
  requirements: string,
  interviewerGuidance: string,
) {
  return request<Position>("/v1/positions", {
    method: "POST",
    body: JSON.stringify({ title, requirements, interviewerGuidance }),
  });
}

export function updatePosition(
  positionId: string,
  title: string,
  requirements: string,
  interviewerGuidance: string,
) {
  return request<Position>(`/v1/positions/${positionId}`, {
    method: "PUT",
    body: JSON.stringify({ title, requirements, interviewerGuidance }),
  });
}

export function deletePosition(positionId: string) {
  return request<void>(`/v1/positions/${positionId}`, { method: "DELETE" });
}

export function matchPosition(
  positionId: string,
  options: { prompt?: string; priority?: string } = {},
) {
  return request<{ items: PositionMatch[] }>(`/v1/positions/${positionId}/match`, {
    method: "POST",
    body: JSON.stringify(options),
  });
}
